import { nextTick } from 'vue'
import { App as CapacitorApp } from '@capacitor/app'
import { PageContexts } from '@/addons/enums'
import { FeLogger } from '@/addons/monitoring'
import { closeNativeBrowserIfVisible } from '@/addons/mobile'
import { popoverController } from '@ionic/vue'
import { createRouter, createWebHistory } from '@ionic/vue-router'
import { RouteRecordRaw } from 'vue-router'
import { beforeEnter, salesDataService } from './guards'
import { Routes, RoutesName } from './routes'
import { cashClosureGuard } from './guards-frontoffice-france'

/**
 * A special flag used to indicate that this route should not be subject to the
 * authorization guard. This is meant to be used as an escape hatch for those
 * situations in which we do not have an updated menu item corresponding to this page.
 * If the page is meant to be accessible by any user, such as the login and dashboard
 * pages, make sure to use the `isUnprivileged` flag insted of this one.
 * Keep in mind that this flag works only if we are not in production. Moreover,
 * when a page is accessed because of this flag, we will log a warning in console and in DataDog.
 *
 * ! Note: this flag is not automatically propagated to child pages. It has to be set manually on each one of them.
 *
 * @see RouteMeta.isUnprivileged
 */
export const __BYPASS_AUTH_GUARD = Symbol.for('BYPASS_AUTH_GUARD')

/**
 * An array of shared local module names used in the 'routes' array.
 * These modules were used across different parts of the routes.
 */
const sharedLocales = [
  'mmfg_menu',
  'mmfg_season',
  'pos_consumer',
  'pos_cash_register',
  'pos_digital_card',
  'pos_endless_aisle',
  'pos_loyalty',
  'pos_loyalty_card',
  'pos_clienteling',
  'pos_catalog',
]

/**
 * @description Routes and views structure.
 * Each top level path is rendered in the "<ion-router-outlet>" defined in the entrypoint component (that is app.vue).
 * This means that the login page and the special "RouterView.vue" view will be rendered in the structure defined in the app.vue file.
 * Nested inside the "/" route, we have all app routes (except for the login one).
 * Those routes are rendered inside a dedicated <ion-router-outlet> (defined in RouterView.vue), since each RouteRecordRaw must define a component.
 * Doing all of this, we can apply a route-level guard and consider it as if it was global (except for the fact that does
 * not affect the /login path) and we can have proper redirects.
 */
const routes: Array<RouteRecordRaw> = [
  // IAM Routes
  {
    path: Routes.CALLBACK,
    component: () => import('@/abs-core/pages/login/CallbackPage.vue'),
    meta: {
      isUnprivileged: true,
      localesModules: [],
    },
  },
  {
    path: Routes.LOGIN,
    component: () => import('@/abs-core/pages/login/LoginPage.vue'),
    meta: {
      isUnprivileged: true,
      localesModules: ['login', 'login-form'],
    },
  },
  {
    path: '/',
    name: 'index',
    component: () => import('@/abs-core/shared/layouts/MainLayout.vue'),
    redirect: Routes.DASHBOARD,
    beforeEnter: beforeEnter,
    children: [
      {
        path: Routes.DASHBOARD,
        component: () => import('@/abs-core/pages/dashboard/DashboardPage.vue'),
        children: [
          {
            name: RoutesName.DASHBOARD_SUBMENU,
            path: Routes.DASHBOARD_SUBMENU,
            component: () => import('@/views/dashboard/submenu.vue'),
            meta: {
              isUnprivileged: true,
              localesModules: [
                ...sharedLocales,
                'mmfg_season',
                'pos_endless_aisle',
                'pos_digital_card',
                'pos_loyalty_card',
                'pos_common',
                'pos_clienteling',
                'modal',
              ],
            },
          },
        ],
        meta: {
          isUnprivileged: true,
          loaderManagedInPage: true,
          localesModules: [
            ...sharedLocales,
            // .
            'mmfg_menu',
            'pos_consumer_languages',
            'pos_sale',
            'codici_lingue',
            'pos_dashboard',
            'pos_clienteling',
            'pos_common',
            'pos_closure',
            'catalog_filter_labels',
            'pos_movement_type',
          ],
        },
      },
      {
        path: Routes.SALES,
        component: () => import('@/abs-core/pages/sales/SalesPage.vue'),
        beforeEnter: salesDataService,
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'codici_lingue',
            'pos_address',
            'pos_barcode',
            'pos_common',
            'pos_common_privacy',
            'pos_consumer_languages',
            'pos_free_return',
            'pos_gift_card',
            'pos_movement_type',
            'pos_oss',
            'pos_payment_type',
            'pos_promo_engine',
            'pos_resi',
            'pos_returns',
            'pos_sale',
            'pos_sosp',
            'pos_stock',
            'pos_suspended_sale',
            'pos_layby',
            'pos_store_credits',
          ],
        },
      },
      {
        path: Routes.RECAP,
        component: () => import('@/abs-core/pages/recap/RecapPage.vue'),
        beforeEnter: [cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          sameAccessRulesAs: [Routes.SALES],
          localesModules: ['pos_document_type'],
        },
      },
      {
        path: Routes.CATALOG,
        component: () => import('@/abs-core/pages/catalog/CatalogPage.vue'),
        meta: {
          context: PageContexts.GENERIC,
          localesModules: [...sharedLocales],
        },
        children: [
          {
            path: '',
            component: () =>
              import('@/abs-core/pages/catalog/CatalogIntroPage.vue'),
            meta: {
              context: PageContexts.GENERIC,
              localesModules: [...sharedLocales],
            },
          },
          {
            path: RoutesName.CATALOG_GUIDED,
            component: () =>
              import('@/abs-core/pages/catalog/CatalogGuidedSearchPage.vue'),
            meta: {
              context: PageContexts.GENERIC,
              localesModules: [...sharedLocales],
            },
          },
          {
            path: RoutesName.CATALOG_CATEGORY,
            component: () =>
              import('@/abs-core/pages/catalog/CatalogCategorySearchPage.vue'),
            meta: {
              context: PageContexts.GENERIC,
              localesModules: [...sharedLocales],
            },
          },
          {
            path: RoutesName.CATALOG_PRODUCT_SEARCH_MOBILE,
            component: () =>
              import('../abs-core/pages/catalog/CatalogProductSearchMobilePage.vue'),
            meta: {
              context: PageContexts.GENERIC,
              localesModules: [...sharedLocales],
            },
          },
        ],
      },
      {
        path: Routes.STOCK,
        component: () => import('@/abs-core/pages/stock/StockPage.vue'),
        meta: {
          context: PageContexts.STOCK,
          localesModules: [
            'mmfg_season',
            'pos_cash_register',
            'pos_digital_card',
            'pos_endless_aisle',
            'pos_loyalty',
            'pos_loyalty_card',
            // .
            'pos_sale',
            'pos_stock',
          ],
        },
      },
      {
        path: Routes.CASH_CHECK,
        component: () =>
          import('@/abs-core/pages/cash-check/CashCheckPage.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_contr_cassa',
            'pos_payment_type',
          ],
        },
      },
      {
        path: Routes.DEPOSITS,
        component: () => import('@/abs-core/pages/deposits/DepositsPage.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_deposit',
            'pos_restore',
          ],
        },
      },
      {
        path: Routes.DEPOSITS_LIST,
        component: () => import('@/views/deposits/deposits-list.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          sameAccessRulesAs: [Routes.DEPOSITS],
          localesModules: [
            'mmfg_menu',
            'pos_cash_register',
            'pos_deposit',
            // .
          ],
        },
      },
      {
        path: Routes.PETTYCASH,
        component: () => import('@/views/petty-cash/petty-cash.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_deposit',
            'pos_restore',
            'pos_petty_cash',
          ],
        },
      },
      {
        path: Routes.PETTY_CASH_LIST,
        component: () => import('@/views/petty-cash/petty-cash-list.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          sameAccessRulesAs: [Routes.PETTYCASH],
          localesModules: [
            ...sharedLocales,
            // .
            'pos_deposit',
            'pos_restore',
            'pos_petty_cash',
          ],
        },
      },
      {
        path: Routes.RETURNS,
        component: () => import('@/views/returns/returns.vue'),
        beforeEnter: [cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_restore',
            'pos_document_type',
            'pos_resi',
            'pos_petty_cash',
            'kpi_selection_form',
            'pos_pdf_sales_report',
            'pos_movement_type',
            'pos_payment_type',
            'pos_returns',
            'pos_store_credits',
          ],
        },
      },
      {
        path: Routes.RECAP_RETURNS,
        component: () => import('@/views/recap-returns.vue'),
        beforeEnter: [cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          sameAccessRulesAs: [Routes.RETURNS],
          localesModules: [
            'pos_loyalty',
            'pos_loyalty_card',
            // .
            'pos_document_type',
            'pos_common',
            'pos_payment_type',
          ],
        },
      },
      {
        path: Routes.SALES_LIST,
        component: () => import('@/views/sales-list/sales-list.vue'),
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_restore',
            'pos_document_type',
            'pos_movement_type',
            'conteggio_banconote',
          ],
        },
      },
      {
        path: Routes.LAYBY_LIST,
        component: () => import('@/views/layby/layby-list.vue'),
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_restore',
            'pos_document_type',
            'pos_movement_type',
            'pos_conteggio_banconote',
            'pos_layby',
          ],
        },
      },
      {
        name: RoutesName.SALES_DETAIL,
        path: Routes.SALES_DETAIL,
        component: () => import('@/views/sales-list/sales-detail.vue'),
        meta: {
          context: PageContexts.FRONTOFFICE,
          sameAccessRulesAs: [Routes.SALES_LIST],
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_movement_type',
            'pos_payment_type',
            'pos_petty_cash',
            'pos_crm_contact_type',
          ],
        },
      },
      {
        path: Routes.PRICE_UPDATE,
        component: () => import('@/views/price-update.vue'),
        meta: {
          context: PageContexts.STOCK,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_update_prices',
          ],
        },
      },
      {
        path: Routes.CASH_CLOSING,
        component: () => import('@/views/cash-closing.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_closure',
          ],
        },
      },
      {
        path: Routes.GIFT_CARD_BALANCE,
        component: () => import('@/views/gift-card-balance.vue'),
        beforeEnter: [cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_tools',
            'pos_giftcard_balance',
          ],
        },
      },
      {
        path: Routes.REFUND_B2E,
        component: () => import('@/views/refund-b2e.vue'),
        beforeEnter: [cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_rimborsi_b2e',
            'pos_resi_b2c',
            'pos_payment_type',
            'pos_sale',
          ],
        },
      },
      {
        path: Routes.SUSPENDED_CREDIT,
        component: () =>
          import('@/views/suspended-credit/suspended-credit.vue'),
        beforeEnter: [salesDataService, cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_payment_type',
            'pos_payment',
            'pos_sosp',
            'pos_movement_type',
            'pos_consumer_languages',
          ],
        },
      },
      {
        path: Routes.SUSPENDED_CREDIT_PERFORMED_PAYMENTS,
        component: () =>
          import('@/views/suspended-credit/performed-payments.vue'),
        beforeEnter: [salesDataService, cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          sameAccessRulesAs: [Routes.SUSPENDED_CREDIT],
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_payment_type',
            'pos_payment',
            'pos_sosp',
            'pos_movement_type',
            'pos_consumer_languages',
          ],
        },
      },
      {
        path: Routes.ALL_SUSPENDED_CREDITS,
        component: () =>
          import('@/views/suspended-credit/all-suspended-credits.vue'),
        beforeEnter: [salesDataService, cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          sameAccessRulesAs: [Routes.SUSPENDED_CREDIT],
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_payment_type',
            'pos_payment',
            'pos_sosp',
            'pos_movement_type',
            'pos_consumer_languages',
          ],
        },
      },
      {
        path: Routes.LAYBY,
        component: () => import('@/views/layby/layby-list.vue'),
        beforeEnter: [cashClosureGuard],
        meta: {
          context: PageContexts.FRONTOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_sale',
            'pos_common',
            'pos_restore',
            'pos_document_type',
            'conteggio_banconote',
            'pos_layby',
          ],
        },
      },
      {
        path: Routes.BANKS,
        component: () => import('@/views/banks.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_common',
            'pos_bank',
          ],
        },
      },
      {
        path: Routes.USER,
        component: () => import('@/abs-core/pages/user/UserPage.vue'),
      },
      {
        path: Routes.USERS,
        component: () => import('@/views/users/users.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_users',
            'codici_lingue',
          ],
        },
      },
      {
        path: Routes.TOOLS,
        component: () => import('@/views/tools/tools.vue'),
        meta: {
          context: PageContexts.BACKOFFICE,
          localesModules: [
            ...sharedLocales,
            // .
            'pos_tools',
            'pos_common',
          ],
        },
      },
      // A special route to render older pages within an ifr ame.
      {
        path: Routes.PREVIOUS_UI_PAGES,
        component: () => import('@/views/iframe.vue'),
      },
      {
        path: Routes.CUSTOMERS,
        component: () =>
          import('@/abs-core/pages/customers/CustomersListPage.vue'),
        meta: {
          localesModules: [...sharedLocales, 'pos_common', 'pos_customers'],
        },
      },
      {
        path: Routes.CUSTOMERS_DETAIL,
        component: () => import('@/abs-core/pages/customers/PageCustomer.vue'),
        meta: {
          localesModules: [...sharedLocales, 'pos_common', 'pos_customers'],
          loaderManagedInPage: true,
        },
        props: true,
      },

      // TODO REMOVE DOWN (setupViews in plugins, after keycloak patch) --------
      // Essendo minifrontend arrivano già provvisti di parte del layout
      {
        path: Routes.CUSTOMERS_TASKS,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageTasks.vue'),
        meta: {
          localesModules: [...sharedLocales, 'pos_common', 'pos_customers'],
        },
        props: true,
      },

      {
        path: Routes.CONVERSATIONS,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageConversations.vue'),
        meta: {
          localesModules: [...sharedLocales, 'pos_common', 'pos_clienteling'],
        },
      },

      {
        path: Routes.INITIATIVE,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageInitiative.vue'),
        meta: {
          localesModules: [
            ...sharedLocales,
            'pos_common',
            'pos_initiative',
            'pos_clienteling',
          ],
        },
      },

      {
        path: Routes.INITIATIVE_SEARCH_CLIENT,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageInitiativeSearchClient.vue'),
        meta: {
          localesModules: [
            'mmfg_menu',
            'pos_common',
            'pos_crm',
            'pos_digital_card',
            'pos_consumer',
            'pos_consumer_languages',
            'pos_customers',
          ],
        },
      },

      {
        path: Routes.INITIATIVE_WELCOME,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageInitiativeWelcome.vue'),
        meta: {
          localesModules: [
            'mmfg_menu',
            'pos_common',
            'pos_crm',
            'pos_digital_card',
            'pos_consumer',
            'pos_consumer_languages',
            'pos_customers',
          ],
        },
      },
      {
        path: Routes.INITIATIVE_LIST,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageInitiativeList.vue'),
        meta: {
          localesModules: [
            'mmfg_menu',
            'pos_common',
            'pos_consumer',
            'pos_initiative',
            'pos_consumer_languages',
          ],
        },
      },
      {
        path: Routes.NOTIFICATIONS,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageNotifications.vue'),
        meta: {
          localesModules: ['mmfg_menu', 'pos_common', 'pos_dashboard'],
        },
      },
      {
        path: Routes.TASKS,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageUserTasks.vue'),
        meta: {
          localesModules: ['mmfg_menu', 'pos_common', 'pos_customers'],
        },
      },
      {
        path: Routes.LOOKBOOK,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageLookbook.vue'),
        meta: {
          localesModules: [...sharedLocales, 'pos_common', 'pos_clienteling'],
        },
      },
      {
        path: Routes.ADD_LOOKBOOK,
        component: () =>
          import('@/abs-plugins/clienteling/pages/PageAddLookbook.vue'),
        meta: {
          localesModules: [...sharedLocales, 'pos_common', 'pos_clienteling'],
        },
      },
      // TODO REMOVE UP ----------------------------
    ],
  },
  {
    // A special route to redirect all unmatched paths to dashboard.
    // This route MUST be defined as the last one.
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: Routes.DASHBOARD,
  },
]

/**
 * Creates a new Vue Router instance with the provided routes.
 * The history mode is set to `createWebHistory` using the `VITE_WEB_APP_PATH_PREFIX` environment variable.
 *
 * Note: we do not use `BASE_URL` as it's different across the iOS and web apps and we want consistent path prefixes.
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_WEB_APP_PATH_PREFIX),
  routes,
})

/**
 * Route history, needed to understand if
 * we can safely use the go-back button
 * (no need to be reactive)
 */
export const routeHistory: string[] = []
router.afterEach((to) => routeHistory.push(to.fullPath))

/**
 * Adds a new route to the Vue Router instance.
 *
 * @param {RouteRecordRaw} route - The new route information, including component, meta, and path.
 * @param {string} [parentName] - The name of the parent route, if the new route is a child route.
 * @returns {void}
 */
export function addRoute(route: RouteRecordRaw, parentName?: string): void {
  router.addRoute(parentName ?? '', route)
}

// TODO REMOVE
// Make sure to check whether current user can access the requested page.
// router.beforeResolve((to) => authorizationGuard(to))

// Declarative ion-popover overlays (e.g. fe-popover) stay mounted outside the
// page tree; dismiss them on navigation so they do not linger after back/forward.
router.afterEach(() => {
  void popoverController.dismiss()
})

export default router
export { Routes, RoutesName, isLoginPage, isRoute } from './routes'

// Handle custom URL protocols (such as `web+com.mmfg.pos:` and `com.mmfg.pos:`)
CapacitorApp.addListener('appUrlOpen', async (event) => {
  if (!event.url) {
    return
  }

  const parsedUrl = new URL(event.url)
  const wouldNavigateTo = parsedUrl.href.replace(`${parsedUrl.protocol}//`, '')
  const navigateTo = wouldNavigateTo.startsWith('/')
    ? wouldNavigateTo
    : `/${wouldNavigateTo}`

  // XXX: temporarly removed for test purpose
  // TODO: we should check the right condition based on real application routes

  // const matchingRoute = router.resolve(wouldNavigateTo)
  // If we were requested one of the allowed routes, we can navigate there.
  // Otherwise we will fallback to the home page.
  // if (
  //   matchingRoute.name &&
  //   CUSTOM_PROTOCOL_HANDLER_ALLOWED_ROUTES.includes(
  //     matchingRoute.name.toString(),
  //   )
  // ) {
  //   navigateTo = wouldNavigateTo
  // }
  try {
    await closeNativeBrowserIfVisible()
  } catch (error) {
    FeLogger.warn('Failed to close the native browser after appUrlOpen', error)
  }
  await nextTick()

  await router.replace(navigateTo)
})
